import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, useState } from "react";
import { fetchExchangeRates, forceFetchExchangeRates, getCachedRateInfo } from "../services/currencyApi";
import * as supabaseApi from "../services/supabaseApi";
import { formatAmountByCurrency, groupByCurrency, calculateTotalInBaseCurrency } from "../utils/currency";
import { gaEvent } from "../utils/ga";

// ============================================================================
// CONSTANTES
// ============================================================================
const INITIAL_RECURRING_INCOME_TEMPLATES = [
  { id: "inc_t1", name: "Sueldo", amount: 3500, active: true },
  { id: "inc_t2", name: "Freelance", amount: 0, active: false }
];

const INITIAL_RECURRING_TEMPLATES = [];

export const CURRENCIES = { PEN: "S/", USD: "$", COP: "COL$", ARS: "AR$", CLP: "CLP$", VEF: "Bs" };

export const CURRENCY_OPTIONS = [
  { code: "PEN", symbol: "S/", label: "Soles (Perú)" },
  { code: "USD", symbol: "$", label: "Dólares (USD)" },
  { code: "COP", symbol: "COL$", label: "Pesos (Colombia)" },
  { code: "ARS", symbol: "AR$", label: "Pesos (Argentina)" },
  { code: "CLP", symbol: "CLP$", label: "Pesos (Chile)" },
  { code: "VEF", symbol: "Bs", label: "Bolívares (Venezuela)" }
];

const DEFAULT_EXCHANGE_RATES = { PEN: 3.75, USD: 1, COP: 4100, ARS: 1000, CLP: 950, VEF: 36 };

const DEFAULT_ENABLED_CURRENCIES = ["PEN", "USD"];

const DEFAULT_STATE = {
  currency: "PEN",
  currencySymbol: "S/",
  usdToPenRate: 3.75,
  exchangeRates: DEFAULT_EXCHANGE_RATES,
  enabledCurrencies: DEFAULT_ENABLED_CURRENCIES,
  showAmounts: true,
  lastSettingsUpdate: 0, // Timestamp de última actualización de settings
  months: [],
  incomes: [],
  expenses: [],
  recurringTemplates: INITIAL_RECURRING_TEMPLATES,
  recurringIncomeTemplates: INITIAL_RECURRING_INCOME_TEMPLATES,
  goals: [],
  contributions: [],
  currencyConversions: [],
  customCategories: [],
};

const getInitialState = () => ({ ...DEFAULT_STATE });

const generateId = (prefix = "id") => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================================================
// REDUCER
// ============================================================================
const ensureRecurringIncomeTemplates = (arr) =>
  Array.isArray(arr) ? arr : INITIAL_RECURRING_INCOME_TEMPLATES;

const controlCashReducer = (state, action) => {
  let newState;
  const recurringIncomeTemplates = ensureRecurringIncomeTemplates(state.recurringIncomeTemplates);

  switch (action.type) {
    case "HYDRATE":
      return { ...DEFAULT_STATE, ...action.payload };

    // ========== MONTHS ==========
    case "CREATE_MONTH":
      newState = {
        ...state,
        months: [...state.months, { id: action.payload.id || generateId("month"), ...action.payload }]
      };
      break;

    case "DELETE_MONTH":
      newState = {
        ...state,
        months: state.months.filter(m => m.id !== action.payload),
        incomes: state.incomes.filter(i => i.monthId !== action.payload),
        expenses: state.expenses.filter(e => e.monthId !== action.payload)
      };
      break;

    // ========== INCOMES ==========
    case "ADD_INCOME":
      newState = {
        ...state,
        incomes: [...state.incomes, { id: action.payload.id || generateId("income"), ...action.payload }]
      };
      break;

    case "UPDATE_INCOME":
      newState = {
        ...state,
        incomes: state.incomes.map(i =>
          i.id === action.payload.incomeId 
            ? { 
                ...i, 
                name: action.payload.name, 
                amount: action.payload.amount,
                currency: action.payload.currency !== undefined ? action.payload.currency : i.currency
              } 
            : i
        )
      };
      break;

    case "DELETE_INCOME":
      newState = {
        ...state,
        incomes: state.incomes.filter(i => i.id !== action.payload.incomeId)
      };
      break;

    // ========== EXPENSES ==========
    case "ADD_EXPENSE":
      newState = {
        ...state,
        expenses: [...state.expenses, { id: action.payload.id || generateId("expense"), ...action.payload }]
      };
      break;

    case "UPDATE_EXPENSE":
      newState = {
        ...state,
        expenses: state.expenses.map(e =>
          e.id === action.payload.id ? { ...e, ...action.payload.patch } : e
        )
      };
      break;

    case "DELETE_EXPENSE":
      newState = {
        ...state,
        expenses: state.expenses.filter(e => e.id !== action.payload)
      };
      break;

    case "TOGGLE_EXPENSE_PAID":
      newState = {
        ...state,
        expenses: state.expenses.map(e =>
          e.id === action.payload
            ? {
                ...e,
                status: e.status === "PENDING" ? "PAID" : "PENDING",
                paidAt: e.status === "PENDING" ? new Date().toISOString() : null
              }
            : e
        )
      };
      break;

    // ========== RECURRING TEMPLATES ==========
    case "CREATE_RECURRING_TEMPLATE":
      newState = {
        ...state,
        recurringTemplates: [
          ...state.recurringTemplates,
          { id: action.payload.id || generateId("template"), ...action.payload }
        ]
      };
      break;

    case "UPDATE_RECURRING_TEMPLATE":
      newState = {
        ...state,
        recurringTemplates: state.recurringTemplates.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload.patch } : t
        )
      };
      break;

    case "DELETE_RECURRING_TEMPLATE":
      newState = {
        ...state,
        recurringTemplates: state.recurringTemplates.filter(t => t.id !== action.payload)
      };
      break;

    case "TOGGLE_RECURRING_TEMPLATE":
      newState = {
        ...state,
        recurringTemplates: state.recurringTemplates.map(t =>
          t.id === action.payload ? { ...t, active: !t.active } : t
        )
      };
      break;

    // ========== RECURRING INCOME TEMPLATES ==========
    case "CREATE_RECURRING_INCOME_TEMPLATE":
      newState = {
        ...state,
        recurringIncomeTemplates: [
          ...recurringIncomeTemplates,
          { id: action.payload.id || generateId("inc_template"), ...action.payload }
        ]
      };
      break;

    case "UPDATE_RECURRING_INCOME_TEMPLATE":
      newState = {
        ...state,
        recurringIncomeTemplates: recurringIncomeTemplates.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload.patch } : t
        )
      };
      break;

    case "DELETE_RECURRING_INCOME_TEMPLATE":
      newState = {
        ...state,
        recurringIncomeTemplates: recurringIncomeTemplates.filter(t => t.id !== action.payload)
      };
      break;

    case "TOGGLE_RECURRING_INCOME_TEMPLATE":
      newState = {
        ...state,
        recurringIncomeTemplates: recurringIncomeTemplates.map(t =>
          t.id === action.payload ? { ...t, active: !t.active } : t
        )
      };
      break;

    // ========== GOALS ==========
    case "CREATE_GOAL":
      newState = {
        ...state,
        goals: [...state.goals, { id: action.payload.id || generateId("goal"), ...action.payload }]
      };
      break;

    case "UPDATE_GOAL":
      newState = {
        ...state,
        goals: state.goals.map(g =>
          g.id === action.payload.id ? { ...g, ...action.payload.patch } : g
        )
      };
      break;

    case "DELETE_GOAL":
      newState = {
        ...state,
        goals: state.goals.filter(g => g.id !== action.payload),
        contributions: state.contributions.filter(c => c.goalId !== action.payload)
      };
      break;

    // ========== CONTRIBUTIONS ==========
    case "ADD_CONTRIBUTION":
      newState = {
        ...state,
        contributions: [
          ...state.contributions,
          { id: action.payload.id || generateId("contribution"), ...action.payload }
        ]
      };
      break;

    case "DELETE_CONTRIBUTION":
      newState = {
        ...state,
        contributions: state.contributions.filter(c => c.id !== action.payload)
      };
      break;

    // ========== CURRENCY CONVERSIONS ==========
    case "ADD_CURRENCY_CONVERSION":
      newState = {
        ...state,
        currencyConversions: [...state.currencyConversions, { id: action.payload.id || generateId("conversion"), ...action.payload }]
      };
      break;

    case "UPDATE_CURRENCY_CONVERSION":
      newState = {
        ...state,
        currencyConversions: state.currencyConversions.map(c =>
          c.id === action.payload.id ? { ...c, ...action.payload.patch } : c
        )
      };
      break;

    case "DELETE_CURRENCY_CONVERSION":
      newState = {
        ...state,
        currencyConversions: state.currencyConversions.filter(c => c.id !== action.payload)
      };
      break;

    // ========== CONFIGURACIÓN (MONEDA) ==========
    case "SET_CURRENCY": {
      const code = action.payload === "S/" || action.payload === CURRENCIES.PEN ? "PEN"
        : action.payload === "$" || action.payload === CURRENCIES.USD ? "USD"
        : CURRENCY_OPTIONS.some(o => o.code === action.payload) ? action.payload : "PEN";
      const opt = CURRENCY_OPTIONS.find(o => o.code === code);
      newState = {
        ...state,
        currency: code,
        currencySymbol: opt?.symbol ?? "S/"
      };
      break;
    }

    case "SET_EXCHANGE_RATE":
      newState = {
        ...state,
        usdToPenRate: Math.max(0.01, parseFloat(action.payload) || 3.75),
        exchangeRates: { ...state.exchangeRates, PEN: Math.max(0.01, parseFloat(action.payload) || 3.75) }
      };
      break;

    case "SET_EXCHANGE_RATES": {
      const rates = { ...DEFAULT_EXCHANGE_RATES, ...action.payload };
      newState = {
        ...state,
        exchangeRates: rates,
        usdToPenRate: rates.PEN ?? state.usdToPenRate
      };
      break;
    }

    case "SET_SHOW_AMOUNTS":
      newState = {
        ...state,
        showAmounts: action.payload
      };
      break;

    case "SET_LAST_SETTINGS_UPDATE":
      newState = {
        ...state,
        lastSettingsUpdate: action.payload
      };
      break;

    case "SET_ENABLED_CURRENCIES": {
      const codes = Array.isArray(action.payload) && action.payload.length > 0
        ? action.payload.filter(c => CURRENCY_OPTIONS.some(o => o.code === c))
        : DEFAULT_ENABLED_CURRENCIES;
      // Si la moneda actual ya no está habilitada, cambiar a la primera habilitada
      const currentStillEnabled = codes.includes(state.currency);
      const nextCurrency = currentStillEnabled ? state.currency : (codes[0] || "PEN");
      const opt = CURRENCY_OPTIONS.find(o => o.code === nextCurrency);
      newState = {
        ...state,
        enabledCurrencies: codes,
        currency: nextCurrency,
        currencySymbol: opt?.symbol ?? "S/"
      };
      break;
    }

    // ========== CUSTOM CATEGORIES ==========
    case "CREATE_CUSTOM_CATEGORY":
      newState = {
        ...state,
        customCategories: [
          ...(state.customCategories || []),
          { id: action.payload.id || generateId("cat"), name: action.payload.name }
        ]
      };
      break;

    case "UPDATE_CUSTOM_CATEGORY":
      newState = {
        ...state,
        customCategories: (state.customCategories || []).map(c =>
          c.id === action.payload.id ? { ...c, name: action.payload.name } : c
        )
      };
      break;

    case "DELETE_CUSTOM_CATEGORY": {
      const { id: catId, reassignTo } = action.payload;
      newState = {
        ...state,
        customCategories: (state.customCategories || []).filter(c => c.id !== catId),
        expenses: state.expenses.map(e =>
          e.category === catId ? { ...e, category: reassignTo || e.category } : e
        ),
        recurringTemplates: state.recurringTemplates.map(t =>
          t.category === catId ? { ...t, category: reassignTo || t.category } : t
        )
      };
      break;
    }

    default:
      return state;
  }

  return newState;
};

// ============================================================================
// CONTEXT
// ============================================================================
const ControlCashContext = createContext();

// ============================================================================
// PROVIDER
// ============================================================================
export const ControlCashProvider = ({ children, userId }) => {
  const [state, dispatch] = useReducer(controlCashReducer, undefined, getInitialState);
  const [isDataLoading, setIsDataLoading] = useState(!!userId);
  const [dataError, setDataError] = useState(null);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const syncDispatchRef = useRef(null);
  const isLoadingRef = useRef(false); // Prevenir múltiples cargas simultáneas
  const stateRef = useRef(state);
  stateRef.current = state; // Mantener ref actualizado con el estado actual

  // Cargar datos desde Supabase al montar
  useEffect(() => {
    if (!userId) {
      setIsDataLoading(false);
      return;
    }
    
    // Prevenir múltiples cargas simultáneas
    if (isLoadingRef.current) {
      console.log("⏸️ Carga ya en progreso, ignorando llamada duplicada");
      return;
    }
    
    setIsDataLoading(true);
    setDataError(null);
    isLoadingRef.current = true;
    
    const load = async () => {
      try {
        const [
          settings,
          months,
          incomes,
          expenses,
          recurringTemplates,
          recurringIncomeTemplates,
          goals,
          contributions,
          currencyConversions,
          customCategories,
        ] = await Promise.all([
          supabaseApi.fetchUserSettings(userId),
          supabaseApi.fetchMonths(userId),
          supabaseApi.fetchIncomes(userId),
          supabaseApi.fetchExpenses(userId),
          supabaseApi.fetchRecurringTemplates(userId),
          supabaseApi.fetchRecurringIncomeTemplates(userId),
          supabaseApi.fetchGoals(userId),
          supabaseApi.fetchContributions(userId),
          supabaseApi.fetchCurrencyConversions(userId),
          supabaseApi.fetchCustomCategories(userId),
        ]);

        const legacyToCode = (c) => (c === "S/" || c === CURRENCIES.PEN ? "PEN" : c === "$" || c === CURRENCIES.USD ? "USD" : (c && CURRENCY_OPTIONS.some(o => o.code === c) ? c : "PEN"));
        const savedCode = legacyToCode(settings?.currency);
        const opt = CURRENCY_OPTIONS.find(o => o.code === savedCode);
        let ratesFromDb = settings?.exchange_rates;
        if (typeof ratesFromDb === "string") {
          try { ratesFromDb = JSON.parse(ratesFromDb); } catch { ratesFromDb = null; }
        }
        const exchangeRates = {
          ...DEFAULT_EXCHANGE_RATES,
          ...(ratesFromDb && typeof ratesFromDb === "object" ? ratesFromDb : {}),
          PEN: parseFloat(ratesFromDb?.PEN ?? settings?.usd_to_pen_rate ?? 3.75) || 3.75
        };
        let enabledCurrencies = DEFAULT_ENABLED_CURRENCIES;
        
        // Verificar si hay monedas pendientes de guardar desde el registro
        let pendingCurrencies = null;
        try {
          const pendingStr = localStorage.getItem('pendingEnabledCurrencies');
          if (pendingStr) {
            pendingCurrencies = JSON.parse(pendingStr);
            console.log("📦 Monedas pendientes encontradas:", pendingCurrencies);
          }
        } catch (err) {
          console.error("Error leyendo monedas pendientes:", err);
        }
        
        const ecRaw = settings?.enabled_currencies;
        
        console.log("🔍 Cargando enabled_currencies desde BD:", {
          ecRaw,
          type: typeof ecRaw,
          isArray: Array.isArray(ecRaw),
          settingsExists: !!settings,
          settingsId: settings?.id,
          updatedAt: settings?.updated_at,
          rawValue: JSON.stringify(ecRaw)
        });
        
        // Manejar si viene como string JSON, array, o null/undefined
        let ecArray = ecRaw;
        if (ecRaw === null || ecRaw === undefined) {
          console.log("⚠️ enabled_currencies es null/undefined, usando default:", DEFAULT_ENABLED_CURRENCIES);
          ecArray = null;
        } else if (typeof ecRaw === "string") {
          try {
            ecArray = JSON.parse(ecRaw);
            console.log("📝 Parseado desde string JSON:", ecArray);
          } catch (err) {
            console.error("❌ Error parseando enabled_currencies como JSON:", err);
            ecArray = null;
          }
        } else if (Array.isArray(ecRaw)) {
          // Ya es un array, usar directamente
          ecArray = ecRaw;
          console.log("✅ enabled_currencies ya es un array:", ecArray);
        }
        
        if (Array.isArray(ecArray) && ecArray.length > 0) {
          const valid = ecArray.filter(c => CURRENCY_OPTIONS.some(o => o.code === c));
          if (valid.length > 0) {
            enabledCurrencies = valid;
            console.log("✅ enabled_currencies cargado correctamente:", enabledCurrencies, "cantidad:", enabledCurrencies.length);
            console.log("🔍 Detalle de monedas cargadas:", enabledCurrencies.map(c => {
              const opt = CURRENCY_OPTIONS.find(o => o.code === c);
              return `${c} (${opt?.label || 'desconocida'})`;
            }).join(', '));
          } else {
            console.warn("⚠️ enabled_currencies no tiene códigos válidos, usando default");
          }
        } else {
          console.log("⚠️ enabled_currencies no es un array válido, usando default:", DEFAULT_ENABLED_CURRENCIES);
        }
        
        // Si hay monedas pendientes del registro y no hay monedas guardadas (o solo defaults),
        // usar las monedas pendientes y guardarlas
        if (pendingCurrencies && Array.isArray(pendingCurrencies) && pendingCurrencies.length > 0) {
          const validPending = pendingCurrencies.filter(c => CURRENCY_OPTIONS.some(o => o.code === c));
          if (validPending.length > 0) {
            // Solo usar las pendientes si no hay monedas guardadas o si son diferentes
            const hasSavedCurrencies = Array.isArray(ecArray) && ecArray.length > 0;
            const pendingDifferent = JSON.stringify([...validPending].sort()) !== JSON.stringify([...enabledCurrencies].sort());
            
            if (!hasSavedCurrencies || pendingDifferent) {
              enabledCurrencies = validPending;
              console.log("✅ Usando monedas pendientes del registro:", enabledCurrencies);
              
              // Guardar las monedas pendientes en la BD
              try {
                await supabaseApi.upsertUserSettings(userId, {
                  enabledCurrencies: enabledCurrencies,
                  currency: enabledCurrencies[0] || "PEN"
                });
                console.log("💾 Monedas pendientes guardadas en BD");
                // Limpiar las monedas pendientes después de guardarlas
                localStorage.removeItem('pendingEnabledCurrencies');
              } catch (err) {
                console.error("❌ Error guardando monedas pendientes:", err);
              }
            } else {
              // Ya están guardadas, solo limpiar el localStorage
              localStorage.removeItem('pendingEnabledCurrencies');
            }
          }
        }
        
        // Log adicional para debugging
        console.log("📊 Resumen de carga:", {
          desdeBD: ecRaw,
          procesado: ecArray,
          final: enabledCurrencies,
          cantidadFinal: enabledCurrencies.length,
          monedasDetalle: enabledCurrencies
        });
        
        // IMPORTANTE: Siempre usar los datos de la BD al cargar inicialmente
        // La lógica de protección solo aplica durante la misma sesión, no al recargar la página
        // Al recargar, el estado se resetea, así que siempre debemos confiar en la BD
        console.log("📥 Usando enabledCurrencies desde BD (carga inicial):", enabledCurrencies);
        
        // Crear categoría por defecto "Gasto fijo" si el usuario no tiene categorías
        let finalCustomCategories = customCategories || [];
        if (finalCustomCategories.length === 0) {
          try {
            console.log("📝 Usuario nuevo sin categorías, creando categoría por defecto 'Gasto fijo'");
            const defaultCategory = await supabaseApi.createCustomCategory(userId, { 
              name: "Gasto fijo" 
            });
            finalCustomCategories = [defaultCategory];
            console.log("✅ Categoría por defecto creada:", defaultCategory);
          } catch (err) {
            console.error("❌ Error creando categoría por defecto:", err);
            // Si falla, continuar sin la categoría por defecto
          }
        }
        
        const hydrateState = {
          currency: opt ? savedCode : "PEN",
          currencySymbol: opt?.symbol ?? "S/",
          usdToPenRate: parseFloat(exchangeRates.PEN ?? settings?.usd_to_pen_rate ?? 3.75),
          exchangeRates,
          enabledCurrencies,
          showAmounts: settings?.show_amounts ?? true,
          months,
          incomes,
          expenses,
          recurringTemplates: recurringTemplates?.length ? recurringTemplates : INITIAL_RECURRING_TEMPLATES,
          recurringIncomeTemplates: recurringIncomeTemplates?.length ? recurringIncomeTemplates : INITIAL_RECURRING_INCOME_TEMPLATES,
          goals: goals || [],
          contributions: contributions || [],
          currencyConversions: currencyConversions || [],
          customCategories: finalCustomCategories,
        };
        dispatchRef.current({ type: "HYDRATE", payload: hydrateState });
      } catch (err) {
        console.error("Error loading data from Supabase:", err);
        setDataError(err.message || "Error al cargar datos");
      } finally {
        setIsDataLoading(false);
        isLoadingRef.current = false;
      }
    };
    load();
  }, [userId]);

  // Dispatch que sincroniza con Supabase (intercepta acciones)
  const syncDispatch = useCallback(
    async (action) => {
      if (!userId) {
        dispatchRef.current(action);
        return;
      }
      const d = dispatchRef.current;
      try {
        switch (action.type) {
          case "CREATE_MONTH": {
            const month = await supabaseApi.createMonth(userId, {
              name: action.payload.name,
              monthNumber: action.payload.monthNumber,
              year: action.payload.year,
            });
            d({ type: "CREATE_MONTH", payload: month });
            gaEvent("create_month");
            return month;
          }
          case "DELETE_MONTH":
            await supabaseApi.deleteMonth(userId, action.payload);
            d(action);
            return;
          case "ADD_INCOME": {
            const income = await supabaseApi.addIncome(userId, {
              ...action.payload,
              currency: action.payload.currency || "PEN"
            });
            d({ type: "ADD_INCOME", payload: income });
            gaEvent("add_income");
            return income;
          }
          case "UPDATE_INCOME":
            await supabaseApi.updateIncome(userId, {
              incomeId: action.payload.incomeId,
              name: action.payload.name,
              amount: action.payload.amount,
              currency: action.payload.currency,
            });
            d(action);
            return;
          case "DELETE_INCOME":
            await supabaseApi.deleteIncome(userId, action.payload.incomeId);
            d(action);
            return;
          case "ADD_EXPENSE": {
            const expense = await supabaseApi.addExpense(userId, {
              monthId: action.payload.monthId,
              name: action.payload.name,
              amount: action.payload.amount,
              currency: action.payload.currency || "PEN",
              category: action.payload.category,
              status: action.payload.status,
              dueDay: action.payload.dueDay,
              isFromRecurring: action.payload.isFromRecurring,
              recurringTemplateId: action.payload.recurringTemplateId,
              remindDaysBefore: action.payload.remindDaysBefore,
              date: action.payload.date,
              paidAt: action.payload.paidAt,
            });
            d({ type: "ADD_EXPENSE", payload: expense });
            gaEvent("add_expense");
            return expense;
          }
          case "UPDATE_EXPENSE":
            await supabaseApi.updateExpense(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_EXPENSE":
            await supabaseApi.deleteExpense(userId, action.payload);
            d(action);
            return;
          case "TOGGLE_EXPENSE_PAID": {
            const expense = await supabaseApi.toggleExpensePaid(userId, action.payload);
            d({ type: "UPDATE_EXPENSE", payload: { id: action.payload, patch: { status: expense.status, paidAt: expense.paidAt } } });
            gaEvent("expense_paid");
            return;
          }
          case "CREATE_RECURRING_TEMPLATE": {
            const tpl = await supabaseApi.createRecurringTemplate(userId, {
              name: action.payload.name,
              amount: action.payload.amount,
              currency: action.payload.currency || "PEN",
              category: action.payload.category,
              dueDay: action.payload.dueDay,
              active: action.payload.active,
              remindDaysBefore: action.payload.remindDaysBefore,
            });
            d({ type: "CREATE_RECURRING_TEMPLATE", payload: tpl });
            gaEvent("recurring_expense_created");
            return tpl;
          }
          case "UPDATE_RECURRING_TEMPLATE":
            await supabaseApi.updateRecurringTemplate(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_RECURRING_TEMPLATE":
            await supabaseApi.deleteRecurringTemplate(userId, action.payload);
            d(action);
            return;
          case "TOGGLE_RECURRING_TEMPLATE": {
            const t = state.recurringTemplates.find((x) => x.id === action.payload);
            if (t) {
              const updated = await supabaseApi.updateRecurringTemplate(userId, action.payload, { active: !t.active });
              d({ type: "UPDATE_RECURRING_TEMPLATE", payload: { id: action.payload, patch: { active: updated.active } } });
            }
            return;
          }
          case "CREATE_RECURRING_INCOME_TEMPLATE": {
            const tpl = await supabaseApi.createRecurringIncomeTemplate(userId, action.payload);
            d({ type: "CREATE_RECURRING_INCOME_TEMPLATE", payload: tpl });
            gaEvent("recurring_income_created");
            return tpl;
          }
          case "UPDATE_RECURRING_INCOME_TEMPLATE":
            await supabaseApi.updateRecurringIncomeTemplate(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_RECURRING_INCOME_TEMPLATE": {
            const templateId = action.payload;
            try {
              await supabaseApi.deleteRecurringIncomeTemplate(userId, templateId);
            } catch (err) {
              // Plantillas default (inc_t1, inc_t2) nunca estuvieron en Supabase
              if (!/^inc_t\d+$/.test(templateId)) throw err;
            }
            d(action);
            return;
          }
          case "TOGGLE_RECURRING_INCOME_TEMPLATE": {
            const t = state.recurringIncomeTemplates.find((x) => x.id === action.payload);
            if (t) {
              try {
                const updated = await supabaseApi.updateRecurringIncomeTemplate(userId, action.payload, { active: !t.active });
                d({ type: "UPDATE_RECURRING_INCOME_TEMPLATE", payload: { id: action.payload, patch: { active: updated.active } } });
              } catch (err) {
                if (!/^inc_t\d+$/.test(action.payload)) throw err;
                d({ type: "UPDATE_RECURRING_INCOME_TEMPLATE", payload: { id: action.payload, patch: { active: !t.active } } });
              }
            }
            return;
          }
          case "CREATE_GOAL": {
            const goal = await supabaseApi.createGoal(userId, action.payload);
            d({ type: "CREATE_GOAL", payload: goal });
            gaEvent("goal_created");
            return goal;
          }
          case "UPDATE_GOAL":
            await supabaseApi.updateGoal(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_GOAL":
            await supabaseApi.deleteGoal(userId, action.payload);
            d(action);
            return;
          case "ADD_CONTRIBUTION": {
            const contrib = await supabaseApi.addContribution(userId, {
              goalId: action.payload.goalId,
              monthId: action.payload.monthId,
              amount: action.payload.amount,
              currency: action.payload.currency || "PEN",
              date: action.payload.date,
              note: action.payload.note,
            });
            d({ type: "ADD_CONTRIBUTION", payload: contrib });
            gaEvent("goal_contribution");
            return contrib;
          }
          case "DELETE_CONTRIBUTION":
            await supabaseApi.deleteContribution(userId, action.payload);
            d(action);
            return;
          case "ADD_CURRENCY_CONVERSION": {
            const conversion = await supabaseApi.addCurrencyConversion(userId, action.payload);
            d({ type: "ADD_CURRENCY_CONVERSION", payload: conversion });
            gaEvent("currency_conversion");
            return conversion;
          }
          case "UPDATE_CURRENCY_CONVERSION":
            await supabaseApi.updateCurrencyConversion(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_CURRENCY_CONVERSION":
            await supabaseApi.deleteCurrencyConversion(userId, action.payload);
            d(action);
            return;
          case "SET_CURRENCY":
          case "SET_EXCHANGE_RATE":
          case "SET_EXCHANGE_RATES":
          case "SET_SHOW_AMOUNTS": {
            // Para estas acciones, NO incluir enabledCurrencies para que Supabase mantenga el valor existente
            let nextCurrency = state.currency;
            
            if (action.type === "SET_CURRENCY") {
              nextCurrency = action.payload;
            }
            
            const next = {
              currency: nextCurrency,
              usdToPenRate: action.type === "SET_EXCHANGE_RATE" ? action.payload : (state.exchangeRates?.PEN ?? state.usdToPenRate),
              exchangeRates: action.type === "SET_EXCHANGE_RATES" ? action.payload : state.exchangeRates,
              showAmounts: action.type === "SET_SHOW_AMOUNTS" ? action.payload : state.showAmounts,
              // NO incluir enabledCurrencies aquí - dejar que Supabase mantenga el valor existente
            };
            
            // Actualizar estado local ANTES de guardar
            d(action);
            
            try {
              console.log(`💾 Guardando ${action.type} en Supabase (sin tocar enabled_currencies)`);
              await supabaseApi.upsertUserSettings(userId, next);
              console.log("✅ Configuración guardada exitosamente");
            } catch (err) {
              console.error("❌ Error guardando configuración:", err);
              throw err;
            }
            return;
          }
          
          case "SET_ENABLED_CURRENCIES": {
            // SOLO para SET_ENABLED_CURRENCIES incluimos enabledCurrencies explícitamente
            const nextEnabledCurrencies = action.payload;
            const codes = action.payload;
            const nextCurrency = codes.includes(state.currency) ? state.currency : (codes[0] || "PEN");
            
            const next = {
              currency: nextCurrency,
              usdToPenRate: state.exchangeRates?.PEN ?? state.usdToPenRate,
              exchangeRates: state.exchangeRates,
              showAmounts: state.showAmounts,
              enabledCurrencies: nextEnabledCurrencies, // SOLO aquí incluimos enabledCurrencies
            };
            
            // Actualizar estado local ANTES de guardar
            d(action);
            
            try {
              console.log("💾 Guardando enabledCurrencies en Supabase:", { enabledCurrencies: next.enabledCurrencies, cantidad: next.enabledCurrencies.length });
              const savedData = await supabaseApi.upsertUserSettings(userId, next);
              console.log("✅ Configuración guardada exitosamente");
              
              // Marcar timestamp de última actualización
              d({ 
                type: "SET_LAST_SETTINGS_UPDATE", 
                payload: Date.now() 
              });
              
              // Verificar que lo que se guardó coincide con lo que enviamos
              if (savedData?.enabled_currencies) {
                const savedArray = Array.isArray(savedData.enabled_currencies) 
                  ? savedData.enabled_currencies 
                  : (typeof savedData.enabled_currencies === 'string' ? JSON.parse(savedData.enabled_currencies) : null);
                
                if (savedArray && JSON.stringify([...savedArray].sort()) !== JSON.stringify([...next.enabledCurrencies].sort())) {
                  console.error("❌ ERROR CRÍTICO: Lo guardado NO coincide con lo enviado!", {
                    enviado: next.enabledCurrencies,
                    guardado: savedArray,
                    cantidadEnviada: next.enabledCurrencies.length,
                    cantidadGuardada: savedArray.length
                  });
                } else {
                  console.log("✅ Verificación: Lo guardado coincide con lo enviado:", savedArray);
                }
              }
            } catch (err) {
              console.error("❌ Error guardando configuración:", err);
              // Revertir el cambio local en caso de error
              d({ 
                type: "SET_ENABLED_CURRENCIES", 
                payload: state.enabledCurrencies 
              });
              throw err;
            }
            return;
          }
          case "CREATE_CUSTOM_CATEGORY": {
            const cat = await supabaseApi.createCustomCategory(userId, { name: action.payload.name });
            d({ type: "CREATE_CUSTOM_CATEGORY", payload: cat });
            return cat;
          }
          case "UPDATE_CUSTOM_CATEGORY":
            await supabaseApi.updateCustomCategory(userId, action.payload.id, { name: action.payload.name });
            d(action);
            return;
          case "DELETE_CUSTOM_CATEGORY":
            await supabaseApi.deleteCustomCategory(userId, action.payload.id);
            d(action);
            return;
          default:
            d(action);
            return;
        }
      } catch (err) {
        console.error("Supabase sync error:", err);
      }
    },
    [userId, state.currency, state.usdToPenRate, state.exchangeRates, state.showAmounts, state.enabledCurrencies, state.recurringTemplates, state.recurringIncomeTemplates]
  );

  syncDispatchRef.current = syncDispatch;

  const apiKey = process.env.REACT_APP_EXCHANGERATE_API_KEY;

  useEffect(() => {
    if (!apiKey?.trim()) return;
    fetchExchangeRates(apiKey).then((result) => {
      if (result?.rates && syncDispatchRef.current) {
        syncDispatchRef.current({ type: "SET_EXCHANGE_RATES", payload: result.rates });
      }
    });
  }, [apiKey]);

  const refreshExchangeRate = useCallback(async () => {
    if (!apiKey?.trim()) return false;
    const result = await forceFetchExchangeRates(apiKey);
    if (result?.rates) {
      syncDispatch({ type: "SET_EXCHANGE_RATES", payload: result.rates });
      return true;
    }
    return false;
  }, [apiKey, syncDispatch]);

  // ========== SELECTORES ==========
  const getMonthById = (id) => {
    return state.months.find(m => m.id === id);
  };

  const getMonthIncomes = (monthId) => {
    return state.incomes.filter(i => i.monthId === monthId);
  };

  const getMonthExpenses = (monthId) => {
    return state.expenses.filter(e => e.monthId === monthId);
  };

  const getMonthContributions = (monthId) => {
    return state.contributions.filter(c => c.monthId === monthId);
  };

  const getMonthConversions = (monthId) => {
    return state.currencyConversions.filter(c => c.monthId === monthId);
  };

  // Obtener totales agrupados por moneda
  const getMonthTotalsByCurrency = (monthId) => {
    const incomes = getMonthIncomes(monthId);
    const expenses = getMonthExpenses(monthId);
    const contributions = getMonthContributions(monthId);
    const conversions = getMonthConversions(monthId);

    // Agrupar por moneda
    const incomesByCurrency = groupByCurrency(incomes);
    const expensesByCurrency = groupByCurrency(expenses);
    const contributionsByCurrency = groupByCurrency(contributions);

    // Procesar conversiones: restar de moneda origen, sumar a moneda destino
    conversions.forEach(conv => {
      const fromCurr = conv.fromCurrency || "PEN";
      const toCurr = conv.toCurrency || "PEN";
      const fromAmt = conv.fromAmount || 0;
      const toAmt = conv.toAmount || 0;

      // Restar de la moneda origen (como si fuera un gasto)
      if (!expensesByCurrency[fromCurr]) expensesByCurrency[fromCurr] = 0;
      expensesByCurrency[fromCurr] += fromAmt;

      // Sumar a la moneda destino (como si fuera un ingreso)
      if (!incomesByCurrency[toCurr]) incomesByCurrency[toCurr] = 0;
      incomesByCurrency[toCurr] += toAmt;
    });

    // Separar gastos pagados y pendientes por moneda
    const paidExpenses = expenses.filter(e => e.status === "PAID");
    const pendingExpenses = expenses.filter(e => e.status === "PENDING");
    
    const paidExpensesByCurrency = groupByCurrency(paidExpenses);
    const pendingExpensesByCurrency = groupByCurrency(pendingExpenses);

    // Agregar conversiones a gastos pagados (las conversiones se consideran "pagadas" inmediatamente)
    conversions.forEach(conv => {
      const fromCurr = conv.fromCurrency || "PEN";
      const fromAmt = conv.fromAmount || 0;
      if (!paidExpensesByCurrency[fromCurr]) paidExpensesByCurrency[fromCurr] = 0;
      paidExpensesByCurrency[fromCurr] += fromAmt;
    });

    // Obtener todas las monedas únicas
    const allCurrencies = new Set([
      ...Object.keys(incomesByCurrency),
      ...Object.keys(expensesByCurrency),
      ...Object.keys(contributionsByCurrency)
    ]);

    const totalsByCurrency = {};
    allCurrencies.forEach(currency => {
      const income = incomesByCurrency[currency] || 0;
      const totalPaid = paidExpensesByCurrency[currency] || 0;
      const totalPending = pendingExpensesByCurrency[currency] || 0;
      const totalCommitted = totalPaid + totalPending;
      const savings = contributionsByCurrency[currency] || 0;
      const availableRemaining = income - totalPaid - savings;
      const finalRemaining = income - totalCommitted - savings;

      totalsByCurrency[currency] = {
        income,
        totalPaid,
        totalPending,
        totalCommitted,
        savings,
        availableRemaining,
        finalRemaining
      };
    });

    return totalsByCurrency;
  };

  // Calcular total estimado en moneda base (para visualización)
  const getMonthTotalsEstimated = (monthId, baseCurrency = "PEN") => {
    const incomes = getMonthIncomes(monthId);
    const expenses = getMonthExpenses(monthId);
    const contributions = getMonthContributions(monthId);
    const conversions = getMonthConversions(monthId);
    const rates = state.exchangeRates || DEFAULT_EXCHANGE_RATES;

    // Procesar conversiones: crear ingresos y gastos virtuales
    const virtualIncomes = conversions.map(conv => ({
      amount: conv.toAmount,
      currency: conv.toCurrency
    }));
    const virtualExpenses = conversions.map(conv => ({
      amount: conv.fromAmount,
      currency: conv.fromCurrency,
      status: "PAID" // Las conversiones se consideran pagadas inmediatamente
    }));

    const allIncomes = [...incomes, ...virtualIncomes];
    const allExpenses = [...expenses, ...virtualExpenses];

    const paidExpenses = allExpenses.filter(e => e.status === "PAID");
    const pendingExpenses = expenses.filter(e => e.status === "PENDING"); // Solo gastos reales pueden estar pendientes

    const totalIncome = calculateTotalInBaseCurrency(allIncomes, baseCurrency, rates);
    const totalPaid = calculateTotalInBaseCurrency(paidExpenses, baseCurrency, rates);
    const totalPending = calculateTotalInBaseCurrency(pendingExpenses, baseCurrency, rates);
    const totalCommitted = totalPaid + totalPending;
    const savings = calculateTotalInBaseCurrency(contributions, baseCurrency, rates);
    const availableRemaining = totalIncome - totalPaid - savings;
    const finalRemaining = totalIncome - totalCommitted - savings;

    return {
      income: totalIncome,
      totalPaid,
      totalPending,
      totalCommitted,
      savings,
      availableRemaining,
      finalRemaining,
      baseCurrency
    };
  };

  // Función legacy para compatibilidad - ahora retorna totales estimados en moneda de visualización
  const getMonthTotals = (monthId) => {
    const baseCurrency = state.currency || "PEN";
    return getMonthTotalsEstimated(monthId, baseCurrency);
  };

  const getMonthPendingBills = (monthId) => {
    return state.expenses.filter(
      e => e.monthId === monthId && e.isFromRecurring && e.status === "PENDING"
    );
  };

  const getEstimatedRecurringTotal = () => {
    return state.recurringTemplates
      .filter(t => t.active)
      .reduce((sum, t) => sum + t.amount, 0);
  };

  const getExpenseById = (id) => {
    return state.expenses.find(e => e.id === id);
  };

  const getGoalCurrentAmount = (goalId) => {
    return state.contributions
      .filter(c => c.goalId === goalId)
      .reduce((sum, c) => sum + c.amount, 0);
  };

  const monthExists = (monthNumber, year) => {
    return state.months.some(m => m.monthNumber === monthNumber && m.year === year);
  };

  // Gastos pendientes que vencen pronto (según remindDaysBefore) - solo del mes actual
  const getUpcomingBills = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();

    const currentMonthIds = state.months
      .filter(m => m.year === currentYear && m.monthNumber === currentMonth)
      .map(m => m.id);

    return state.expenses
      .filter(e => e.status === "PENDING" && e.dueDay != null && currentMonthIds.includes(e.monthId))
      .map(expense => {
        const remindDays = expense.remindDaysBefore ?? 0;
        const dueDay = expense.dueDay;
        const daysUntilDue = dueDay - currentDay;
        const shouldRemind = daysUntilDue <= remindDays && daysUntilDue >= -1;
        return { ...expense, shouldRemind, daysUntilDue };
      })
      .filter(e => e.shouldRemind)
      .sort((a, b) => a.daysUntilDue - b.daysUntilDue);
  };

  // Meses ordenados por fecha descendente (más reciente primero)
  const getMonthsSortedByDate = () => {
    return [...state.months].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.monthNumber - a.monthNumber;
    });
  };

  const rates = state.exchangeRates || DEFAULT_EXCHANGE_RATES;
  const penRate = rates.PEN || state.usdToPenRate || 3.75;
  const displayRate = rates[state.currency] ?? (state.currency === "USD" ? 1 : penRate);

  // Función legacy para compatibilidad - convierte de PEN a moneda de visualización
  // DEPRECATED: Usar formatAmountByCurrency directamente con la moneda original
  const toDisplayAmount = (amountInPen) => {
    if (!amountInPen || isNaN(amountInPen)) return 0;
    if (state.currency === "PEN" || !displayRate) return amountInPen;
    return amountInPen * (displayRate / penRate);
  };

  // Función legacy para compatibilidad - convierte de moneda de visualización a PEN
  // DEPRECATED: Ya no se debe convertir al guardar, guardar directamente con currency
  const toStorageAmount = (amountFromUser) => {
    const n = parseFloat(amountFromUser);
    if (isNaN(n)) return 0;
    if (state.currency === "PEN" || !displayRate) return n;
    return n * (penRate / displayRate);
  };

  // Formatear monto para mostrar - ahora acepta currency como parámetro opcional
  // Si no se pasa currency, asume que es un monto en PEN (legacy) y convierte a moneda de visualización
  const formatDisplayAmount = (amount, hideValue, currency) => {
    const shouldHide = hideValue !== undefined ? hideValue : !state.showAmounts;
    if (shouldHide) return "••••";
    
    // Si se pasa currency, usar la moneda original
    if (currency) {
      return formatAmountByCurrency(amount, currency, shouldHide);
    }
    
    // Legacy: convertir desde PEN a moneda de visualización
    const displayAmount = toDisplayAmount(amount);
    const formatted = new Intl.NumberFormat("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(displayAmount);
    return `${state.currencySymbol} ${formatted}`;
  };

  const formatUserAmount = (amount) => {
    const n = parseFloat(amount);
    if (isNaN(n)) return `${state.currencySymbol} 0`;
    const formatted = new Intl.NumberFormat("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
    return `${state.currencySymbol} ${formatted}`;
  };

  const value = {
    // Estado
    state,
    dispatch: syncDispatch,
    isDataLoading,
    dataError,
    // Tipo de cambio API
    hasExchangeRateApiKey: !!apiKey?.trim(),
    refreshExchangeRate,
    getCachedRateInfo,
    // Moneda y conversión
    toDisplayAmount, // Legacy - usar formatAmountByCurrency directamente
    toStorageAmount, // Legacy - ya no convertir al guardar
    formatDisplayAmount, // Actualizado para aceptar currency
    formatUserAmount,
    // Selectores
    getMonthById,
    getMonthIncomes,
    getMonthExpenses,
    getMonthContributions,
    getMonthConversions, // Nuevo - obtiene conversiones de un mes
    getMonthTotals, // Legacy - retorna totales estimados en moneda de visualización
    getMonthTotalsByCurrency, // Nuevo - retorna totales separados por moneda (incluye conversiones)
    getMonthTotalsEstimated, // Nuevo - retorna totales estimados en moneda base específica (incluye conversiones)
    getMonthPendingBills,
    getEstimatedRecurringTotal,
    getExpenseById,
    getGoalCurrentAmount,
    monthExists,
    getUpcomingBills,
    getMonthsSortedByDate
  };

  return (
    <ControlCashContext.Provider value={value}>
      {children}
    </ControlCashContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================
export const useControlCash = () => {
  const context = useContext(ControlCashContext);
  if (!context) {
    throw new Error("useControlCash must be used within ControlCashProvider");
  }
  return context;
};
